import React, { useContext, useEffect, useState, useRef } from "react";
import NotificationDetailContext from "features/metadesk/Notification/NotificationDetail/NotificationDetail.context";
import { useOutletContext, useParams, useLocation } from "react-router-dom";
import { Checkbox } from "@metacrm/metacrm-material-ui/dist/Checkbox";
import MetaCRMTag from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";

import { Box } from "@mui/material";
import {
  StyledWhiteContainer,
  StyledWhiteInsidePadding,
  StyledSettingItemTitle,
  StyledSettingItemDescription,
  StyledInputTitle,
  StyledSettingItemInfo,
  StyledSettingItemTinyTitle,
  StyledSettingItemLink,
} from "./NotificationInfoArea.styles";
import {
  selectEcosystemName,
  selectWidgetConnected,
  selectXMTPConnected,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { useIntl } from "react-intl";
import useNotificationCenterState from "features/metadesk/Notification/NotificationDetail/NotificationDetail.hooks";
import { Controller } from "react-hook-form";
import DateTimePicker from "features/metadesk/Notification/NotificationDetail/components/DateTimePicker/DateTimePicker";
import FixedButton from "features/metadesk/settings/common/FixedButton";
import Button, {
  COLOR,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import {
  AutoCompleteDropdown,
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import axios from "axios";
import { apiUrl } from "features/configure";
import SelectChannelItem from "../SelectChannelItem/SelectChannelItem";
import { formatNumberWithComma } from "features/helpers/utils";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { useTheme } from "@mui/styles";
import { id } from "date-fns/locale";
import { useSelector, useDispatch } from "react-redux";
import { get } from "lodash-es";
import { alpha } from "@mui/material/styles";
import { InputField } from "@metacrm/metacrm-material-ui/dist/InputField";
import { enqueueSnackbar } from "features/common/redux/actions";
import Switch from "@metacrm/metacrm-material-ui/dist/Switch";
import Collapse from "@mui/material/Collapse";
import { BasicDialog } from "@metacrm/metacrm-material-ui/dist/Dialog";
import moment from "moment";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import useUnload from "common/useUnload";
import Prompt from "features/common/Prompt";

import { ImageUploader } from "@metacrm/metacrm-material-ui/dist/ImageUploader";
import { color } from "echarts";
import useNavigateEntity from "hooks/useNavigateEntity";
import { useFetchTags } from "features/metadesk/redux/fetchTags";

const isToday = (date) => {
  if (!date) return false; // 確保 date 有值
  // 如果 date 是字符串，先將其轉換為 Date 對象
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  const today = new Date();
  return (
    parsedDate.getDate() === today.getDate() &&
    parsedDate.getMonth() === today.getMonth() &&
    parsedDate.getFullYear() === today.getFullYear()
  );
};

const getNumberByName = (targetArray, name) => {
  const result = targetArray.find((item) => item.name === name);
  return result ? result.number : null; // 若找不到則回傳 null
};

const SettingItemLink = ({ children, ...props }) => {
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(children); // 檢查是否為 Email
  const href = isEmail ? `mailto:${children}` : children;
  return (
    <StyledSettingItemLink
      href={href}
      target={isEmail ? "_self" : "_blank"}
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </StyledSettingItemLink>
  );
};

const NotificationInfoArea = ({ status }) => {
  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    errorsInfo,
    isDirty,
    isValid,
    reset,
    setValue,
    getValues,
    trigger,
    set,
    uploadImgLoading,
    setUploadImgLoading,
    imagesError,
    setImagesError,
    targetAudiencesLoading,
    setTargetAudiencesLoading,
  } = useNotificationCenterState();

  const [loading, setLoading] = useState(false);

  const { navigateEntity, linkToEntityNew } = useNavigateEntity();

  const [expanded, setExpanded] = useState(false);
  const { notificationId } = useParams();
  const location = useLocation();

  const readOnly = status === "sent" || status === "archived" ? true : false;
  const isCreate = status === "draft" && !notificationId;

  const theme = useTheme();
  const widgetConnected = useSelector(selectWidgetConnected);
  const xmtpConnected = useSelector(selectXMTPConnected);
  const { formData, setFormData, initData } = useContext(
    NotificationDetailContext
  );

  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceOptions, setAudienceOptions] = useState([]);
  const audienceOptionsLoading = audienceOpen && audienceOptions.length === 0;
  const [entityName] = useOutletContext();
  const dispatch = useDispatch();
  const [timePassDialogShow, setTimePassDialogShow] = useState(false);
  const confirm = useConfirm();
  const isMounted = useRef(false);

  const { tags } = useFetchTags();

  useUnload((e) => {
    if (isDirty) {
      e.preventDefault();
      e.returnValue = "";
    }
  });

  useEffect(() => {
    if (!isMounted.current) {
      if (!isCreate && formData) {
        reset(formData);
      }
      isMounted.current = true;
    }
  }, [isCreate, formData]);

  useEffect(() => {
    if (!formData.isSchedule && isCreate) {
      setFormData((prev) => ({ ...prev, sendTime: "" }));
      setValue("sendTime", "", { shouldDirty: isCreate ? false : true });
    }
  }, [formData.isSchedule, isCreate]);

  useEffect(() => {
    setFormData((prev) => {
      const newChannels = {
        ...prev.channels,
        widget: prev.channels.widget && widgetConnected,
        xmtp: prev.channels.xmtp && xmtpConnected,
      };
      setValue("channels", newChannels, { shouldDirty: false });

      return {
        ...prev,
        channels: newChannels,
      };
    });
  }, [widgetConnected, xmtpConnected]);

  const xmtpIcons = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {[
          {
            image: require("assets/img/xmtp_dapp1.png"),
            name: "Coinbase",
          },
          {
            image: require("assets/img/xmtp_dapp2.png"),
            name: "Hey",
          },
          {
            image: require("assets/img/xmtp_dapp3.png"),
            name: "",
          },
          {
            image: require("assets/img/xmtp_dapp4.png"),
            name: "Paragraph",
          },
          {
            image: require("assets/img/xmtp_dapp5.png"),
            name: "Phaver",
          },
          {
            image: require("assets/img/xmtp_dapp6.png"),
            name: "Yup",
          },
          {
            image: require("assets/img/xmtp_dapp7.png"),
            name: "Converse",
          },
        ].map((dapp) => (
          <img src={dapp.image} alt={dapp.name} />
        ))}
      </Box>
    );
  };

  const channelData = [
    {
      title: formatMessage({ id: "campaignPerformance.tab.widget.title" }),
      id: "widget",
      errors: get(errorsInfo, "channels"),
      disabled: !widgetConnected,
      selected: formData.channels.widget,
      iconContent: widgetConnected ? null : (
        <IconTooltip
          customColor={theme.customColors.pink[600]}
          content={formatMessage({ id: "notification.create.widgetTooltip" })}
        />
      ), // 假設是你們系統的 IconTooltip 元件
      estimatedReachContent: widgetConnected ? (
        <Box display={"flex"} alignItems={"center"}>
          {formatMessage({ id: "notification.create.varies" })}{" "}
          <IconTooltip
            sx={{ marginLeft: "4px" }}
            content={formatMessage({ id: "notification.create.variesToolTip" })}
          />
        </Box>
      ) : (
        "-"
      ),
    },
    {
      title: formatMessage({ id: "global.commonWord.discord" }),
      id: "discord",
      errors: get(errorsInfo, "channels"),
      disabled: false,
      selected: formData.channels.discord,
      estimatedReachContent: formatNumberWithComma(
        getNumberByName(formData.estimatedReachWhenSent, "discord")
      ),
    },
    {
      title: formatMessage({ id: "global.commonWord.telegram" }),
      id: "telegram",
      errors: get(errorsInfo, "channels"),
      disabled: false,
      selected: formData.channels.telegram,
      estimatedReachContent: formatNumberWithComma(
        getNumberByName(formData.estimatedReachWhenSent, "telegram")
      ),
    },
    {
      title: formatMessage({ id: "global.form.email.label" }),
      id: "email",
      errors: get(errorsInfo, "channels"),
      disabled: false,
      selected: formData.channels.email,
      estimatedReachContent: formatNumberWithComma(
        getNumberByName(formData.estimatedReachWhenSent, "email")
      ),
    },
    {
      title: formatMessage({ id: "integration.xmtp.name" }),
      id: "xmtp",
      errors: get(errorsInfo, "channels"),
      disabled: !xmtpConnected,
      selected: formData.channels.xmtp,
      iconContent: xmtpConnected ? (
        <Box display={"flex"} alignItems={"center"}>
          {xmtpIcons()}
          <IconTooltip
            sx={{ marginLeft: "8px" }}
            content={formatMessage({ id: "notification.create.xmtpToolTip" })}
          />
        </Box>
      ) : (
        <IconTooltip
          customColor={theme.customColors.pink[600]}
          content={formatMessage({ id: "notification.create.xmtpTooltip" })}
        />
      ),
      estimatedReachContent: formatNumberWithComma(
        getNumberByName(formData.estimatedReachWhenSent, "xmtp")
      ),
    },
  ];

  const handleChangeChannel = (e, onFormChange, formValue) => {
    setFormData((prev) => ({
      ...prev,
      channels: { ...prev.channels, [e.target.name]: e.target.checked },
    }));
    onFormChange({
      ...formValue,
      [e.target.name]: e.target.checked,
    });
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFieldChangeEmail = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      emailSetting: { ...formData.emailSetting, [name]: value },
    });
  };

  const handleChangeStartFrom = (value) => {
    setFormData((prev) => ({ ...prev, sendTime: value }));
  };

  const handleChangeTargetAudiences = (event, newValue) => {
    // setViewTarget(value.value);
    setFormData((prev) => ({ ...prev, targetAudiences: newValue }));
  };
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const fetchData = async () => {
      setTargetAudiencesLoading(true);
      try {
        const { data } = await axios.post(
          `${apiUrl}/api/notification/groups/estimatedReach`,
          {
            entityName: entityName,
            groupIds: formData.targetAudiences.some((item) => item.isAllOption)
              ? ["all"]
              : formData.targetAudiences.map((item) => item.value),
          },
          { signal }
        );
        setFormData((prevFormData) => ({
          ...prevFormData,
          estimatedReachWhenSent: prevFormData.estimatedReachWhenSent.map(
            (item) => ({
              ...item,
              number: data[item.name] ?? null, // 如果 data 有該 key，則取值，否則設為 null
            })
          ),
        }));
      } catch (error) {
        if (error.code === "ERR_CANCELED") return;
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
            options: {
              variant: "error",
            },
          })
        );
      } finally {
        setTargetAudiencesLoading(false);
      }
    };

    if (!readOnly) {
      fetchData();
    }
    return () => {
      controller.abort();
    };
  }, [formData.targetAudiences, readOnly]);

  useEffect(() => {
    const fetchData = async () => {
      setTargetAudiencesLoading(true);
      try {
        const { data } = await axios.get(
          `${apiUrl}/api/notification/emailSetting/${entityName}`
        );

        setFormData((prevFormData) => ({
          ...prevFormData,
          emailSetting: {
            companyAddress: get(data, "companyAddress", ""),
            privacyPolicyLink: get(data, "privacyPolicyLink", ""),
            emailAddress: get(data, "emailAddress", ""),
            websiteURL: get(data, "websiteURL", ""),
            socialMediaLink: get(data, "socialMediaLink", ""),
          },
        }));
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
            options: {
              variant: "error",
            },
          })
        );
      } finally {
        setTargetAudiencesLoading(false);
      }
    };

    fetchData();
  }, []);

  const sendInfo = async ({ isDraft = false }) => {
    // 這邊的 isDraft 是儲存為draft的意思
    if (!isDraft && formData.isSchedule) {
      const specificTime = moment(formData.sendTime);
      const now = moment();
      const isAfter = specificTime.isAfter(now);
      if (!isAfter) {
        setTimePassDialogShow(true);
        return;
      }
    }

    if (status === "scheduled" || (status === "draft" && notificationId)) {
      try {
        setLoading(true);
        const { data } = await axios.post(
          `${apiUrl}/api/notification/${entityName}/edit`,
          {
            ...formData,
            notificationId: notificationId,
            status: isDraft ? "draft" : "scheduled",
          }
        );
        await reset(formData);
        setTimeout(() => {
          navigateEntity("/notification/notification-center");
        }, 0);
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
            options: {
              key: new Date().getTime() + Math.random(),
              variant: "error",
            },
          })
        );
      } finally {
        setLoading(false);
      }
    }

    if (isCreate) {
      try {
        setLoading(true);
        const { data } = await axios.post(
          `${apiUrl}/api/notification/${entityName}/create`,
          { ...formData, status: isDraft ? "draft" : null }
        );
        await reset(formData);
        setTimeout(() => {
          navigateEntity("/notification/notification-center");
        }, 0);
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
            options: {
              key: new Date().getTime() + Math.random(),
              variant: "error",
            },
          })
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const sendInfoConfirm = async () => {
    confirm({
      color: COLOR.SECONDARY,
      cancelColor: COLOR.SECONDARY,
      title: formatMessage({
        id: "notification.create.sendReminder",
      }),
      content: formatMessage({
        id: "notification.create.sendReminderDescription",
      }),
      confirmText: formatMessage({
        id: "global.btn.sendNow",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(async () => {
        await sendInfo({ isDraft: false });
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  useEffect(() => {
    let active = true;

    if (!audienceOptionsLoading) return;

    (async () => {
      const tagOptions = (tags?.[entityName] || []).map((item) => ({
        ...item,
        label: item.value,
        value: item._id,
      }));

      // await sleep(1e3); // For demo purposes.
      if (active) {
        setAudienceOptions([
          {
            label: formatMessage({ id: "global.filter.all" }),
            isAllOption: true,
            value: "",
          },
          ...tagOptions,
        ]);
      }
    })();

    return () => {
      active = false;
    };
  }, [audienceOptionsLoading, tags, entityName]);

  useEffect(() => {
    if (!audienceOpen) {
      setAudienceOptions([]);
    }
  }, [audienceOpen]);

  const handleToggle = () => {
    setAudienceOpen((prev) => !prev);
  };

  const renderSelectChannels = ({
    field: { onChange: onFormChange, value: formValue, ...field },
  }) => {
    const renderData = readOnly
      ? channelData?.filter((channel) => formData?.channels[channel.id])
      : channelData;
    return renderData.map((channelDataInfo, index) => {
      return (
        <SelectChannelItem
          key={index}
          channelDataInfo={channelDataInfo}
          index={index}
          onChange={(e) => handleChangeChannel(e, onFormChange, formValue)}
          loading={targetAudiencesLoading}
          readOnly={readOnly}
        />
      );
    });
  };

  const uploadFile = async (file) => {
    if (file) {
      setUploadImgLoading(true);
      let imgFormData = new FormData();
      imgFormData.append("notification", file);
      imgFormData.append("entityName", entityName);
      try {
        const {
          data: { notification },
        } = await axios.post(
          `${apiUrl}/api/gcp/uploadNotification`,
          imgFormData
        );
        setFormData((prev) => ({ ...prev, image: notification[0] }));
        setValue("image", notification[0], { shouldDirty: true });
      } catch (error) {
        dispatch(
          enqueueSnackbar({
            message: get(error, "response.data.error", "error"),
            options: {
              variant: "error",
            },
          })
        );
      } finally {
        setUploadImgLoading(false);
      }
    }
  };

  const handleDeleteImage = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, image: "" }));
    setValue("image", "", { shouldDirty: true });
  };

  const handleMoveToDraft = async () => {
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/moveToDraft`
      );
      navigateEntity("/notification/notification-center");
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
          options: {
            variant: "error",
          },
        })
      );
    }
  };

  const renderDraftButton = () => {
    if (status === "draft") {
      return (
        <Button
          sx={{ marginLeft: "8px" }}
          variant={VARIANT.OUTLINED}
          disabled={loading}
          onClick={() => sendInfo({ isDraft: true })}
        >
          {formatMessage({ id: "global.btn.saveDraft" })}
        </Button>
      );
    }
    if (status === "scheduled") {
      return (
        <Button
          sx={{ marginLeft: "8px" }}
          variant={VARIANT.OUTLINED}
          onClick={handleMoveToDraft}
          disabled={loading}
        >
          {formatMessage({ id: "global.function.moveToDraft" })}
        </Button>
      );
    }
  };

  const renderSendButton = () => {
    if (status === "draft") {
      if (formData.isSchedule) {
        return (
          <Button
            sx={{ width: "120px", marginLeft: "8px" }}
            onClick={handleSubmit(sendInfo)}
            disabled={loading}
          >
            {formatMessage({ id: "global.btn.schedule" })}
          </Button>
        );
      } else {
        return (
          <Button
            sx={{ width: "120px", marginLeft: "8px" }}
            onClick={handleSubmit(sendInfoConfirm)}
            disabled={loading}
          >
            {formatMessage({ id: "global.btn.sendNow" })}
          </Button>
        );
      }
    }
    if (status === "archived") {
      return (
        <Button sx={{ width: "120px", marginLeft: "8px" }} disabled>
          {formatMessage({ id: "global.campaignStatus.archived" })}
        </Button>
      );
    }

    if (status === "scheduled") {
      if (isDirty) {
        return (
          <Button
            sx={{ marginLeft: "8px" }}
            onClick={
              formData.isSchedule
                ? handleSubmit(sendInfo)
                : handleSubmit(sendInfoConfirm)
            }
            disabled={loading}
          >
            {formData.isSchedule
              ? formatMessage({ id: "global.btn.saveChanges" })
              : formatMessage({ id: "global.btn.sendNow" })}
          </Button>
        );
      } else {
        return (
          <Button sx={{ width: "120px", marginLeft: "8px" }} disabled>
            {formatMessage({ id: "global.campaignStatus.scheduled" })}
          </Button>
        );
      }
    }

    if (status === "sent") {
      return (
        <Button sx={{ width: "120px", marginLeft: "8px" }} disabled>
          {formatMessage({ id: "global.btn.sent" })}
        </Button>
      );
    }
  };

  const handleArchive = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/archive`
      );
      navigateEntity("/notification/notification-center");
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
          options: {
            variant: "error",
          },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveConfirm = () => {
    confirm({
      title: formatMessage({
        id: "notification.create.archiveReminder",
      }),
      content: formatMessage({
        id: "notification.create.archiveReminderDescription",
      }),
      confirmText: formatMessage({
        id: "notification.create.archiveReminderbtn",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(async () => {
        await handleArchive();
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/api/notification/${entityName}/${notificationId}/delete`
      );
      navigateEntity("/notification/notification-center");
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: get(error, "response.data.error", "error"),
          options: {
            variant: "error",
          },
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = () => {
    confirm({
      title: formatMessage({
        id: "notification.creation.deleteReminderTitle",
      }),
      content: formatMessage({
        id: "notification.creation.deleteReminderDescription",
      }),
      confirmText: formatMessage({
        id: "popup.confirmToDelete",
      }),
      cancelText: formatMessage({
        id: "global.btn.cancel",
      }),
    })
      .then(async () => {
        await handleDelete();
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  const renderLeftButton = () => {
    if (status === "sent") {
      return (
        <Button
          color={COLOR.ERROR}
          variant={VARIANT.TEXT}
          onClick={handleArchiveConfirm}
          startIcon={<i className="meta-crm-icon-ic_Archive font-size-16" />}
          sx={{
            paddingLeft: "0",
            paddingRight: 0,
          }}
        >
          {formatMessage({ id: "notification.btn.archive" })}
        </Button>
      );
    }

    if (status === "scheduled" || (status === "draft" && !isCreate)) {
      return (
        <Button
          color={COLOR.ERROR}
          variant={VARIANT.TEXT}
          onClick={handleDeleteConfirm}
          startIcon={<i className="meta-crm-icon-ic_trash font-size-16" />}
          sx={{
            paddingLeft: "0",
            paddingRight: 0,
          }}
        >
          {formatMessage({ id: "notification.btn.delete" })}
        </Button>
      );
    }
  };

  useEffect(() => {
    if (!formData.ctaButtonEnabled) {
      setFormData((prev) => ({
        ...prev,
        buttonText: "",
        buttonUrl: "",
      }));
      setValue("buttonText", "", { shouldDirty: false });
      setValue("buttonUrl", "", { shouldDirty: false });
    }
  }, [formData.ctaButtonEnabled]);

  useEffect(() => {
    if (!formData.channels.email) {
      setFormData((prev) => ({
        ...prev,
        ctaButtonEnabled: false,
      }));
      setValue("ctaButtonEnabled", false, { shouldDirty: false });
    }
  }, [formData.channels.email]);

  const renderCTAButtonArea = () => {
    if (readOnly) {
      if (formData.channels.email) {
        return (
          <StyledWhiteInsidePadding
            sx={{
              borderTop: readOnly
                ? "none"
                : `1px solid ${theme.customColors.grey[300]}`,
            }}
          >
            {formData.ctaButtonEnabled && (
              <>
                <Box mb={3}>
                  <StyledSettingItemTitle readonly>
                    {formatMessage({
                      id: "notification.create.ctaBtn.text",
                    })}
                  </StyledSettingItemTitle>
                  <StyledSettingItemInfo>
                    {formData.buttonText}
                  </StyledSettingItemInfo>
                </Box>

                <Box>
                  <StyledSettingItemTitle readonly>
                    {formatMessage({
                      id: "notification.create.ctaBtn.url",
                    })}
                  </StyledSettingItemTitle>
                  <StyledSettingItemLink>
                    {formData.buttonUrl}
                  </StyledSettingItemLink>
                </Box>
              </>
            )}
          </StyledWhiteInsidePadding>
        );
      }
    } else {
      if (formData.channels.email) {
        return (
          <StyledWhiteInsidePadding
            sx={{
              borderTop: readOnly
                ? "none"
                : `1px solid ${theme.customColors.grey[300]}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box color={theme.customColors.grey[700]}>
                {formatMessage({ id: "notification.create.ctaBtn" })}
              </Box>
              <Box>
                <Controller
                  name="ctaButtonEnabled"
                  control={control}
                  render={({
                    field: {
                      onChange: onFormChange,
                      value: formValue,
                      ...field
                    },
                  }) => {
                    return (
                      <Switch
                        checked={formData.ctaButtonEnabled}
                        iosSwitch
                        onChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            ctaButtonEnabled: value,
                          }));
                          onFormChange(value);
                        }}
                      />
                    );
                  }}
                />
              </Box>
            </Box>
            {formData.ctaButtonEnabled && (
              <>
                <Box mt="20px" mb="20px">
                  <StyledInputTitle isRequired errors={errorsInfo.buttonText}>
                    {formatMessage({ id: "notification.create.ctaBtn.text" })}
                  </StyledInputTitle>
                  <InputField
                    maxLength={10}
                    placeholder={formatMessage({
                      id: "notification.create.ctaBtn.placeholder",
                    })}
                    name="buttonText"
                    control={control}
                    value={formData.buttonText}
                    onChange={handleFieldChange}
                    errors={get(errorsInfo, "buttonText.message")}
                    rules={{
                      required: {
                        value: true,
                        message: formatMessage({
                          id: "notification.create.blankFieldError",
                        }),
                      },
                    }}
                  />
                </Box>

                <Box>
                  <StyledInputTitle isRequired errors={errorsInfo.buttonUrl}>
                    {formatMessage({ id: "notification.create.ctaBtn.url" })}
                  </StyledInputTitle>
                  <InputField
                    placeholder={formatMessage({
                      id: "notification.create.ctaBtn.urlPlaceholder",
                    })}
                    name="buttonUrl"
                    control={control}
                    value={formData.buttonUrl}
                    onChange={handleFieldChange}
                    errors={get(errorsInfo, "buttonUrl.message")}
                    rules={{
                      required: {
                        value: true,
                        message: formatMessage({
                          id: "notification.create.blankFieldError",
                        }),
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </StyledWhiteInsidePadding>
        );
      }
    }
  };

  const renderEmailBottomArea = () => {
    if (formData.channels.email) {
      if (readOnly) {
        return (
          <StyledWhiteContainer>
            <StyledWhiteInsidePadding>
              <StyledSettingItemTinyTitle sx={{ marginBottom: "16px" }}>
                {formatMessage({ id: "notification.create.emailFooter" })}
              </StyledSettingItemTinyTitle>

              <Box mb={3}>
                <StyledSettingItemTitle readonly>
                  {formatMessage({
                    id: "notification.create.emailFooter.address",
                  })}
                </StyledSettingItemTitle>
                <StyledSettingItemInfo>
                  {formData.emailSetting.companyAddress}
                </StyledSettingItemInfo>
              </Box>

              <Box mb={3}>
                <StyledSettingItemTitle readonly>
                  {formatMessage({
                    id: "notification.create.emailFooter.ppLink",
                  })}
                </StyledSettingItemTitle>
                <SettingItemLink>
                  {formData.emailSetting.privacyPolicyLink}
                </SettingItemLink>
              </Box>

              <Box mb={3}>
                <StyledSettingItemTitle readonly>
                  {formatMessage({
                    id: "notification.create.emailFooter.email",
                  })}
                </StyledSettingItemTitle>
                <SettingItemLink>
                  {formData.emailSetting.emailAddress}
                </SettingItemLink>
              </Box>

              <Box mb={3}>
                <StyledSettingItemTitle readonly>
                  {formatMessage({
                    id: "notification.create.emailFooter.webUrl",
                  })}
                </StyledSettingItemTitle>
                <SettingItemLink>
                  {formData.emailSetting.websiteURL}
                </SettingItemLink>
              </Box>

              <Box>
                <StyledSettingItemTitle readonly>
                  {formatMessage({
                    id: "notification.create.emailFooter.socialLinks",
                  })}
                </StyledSettingItemTitle>
                <SettingItemLink>
                  {formData.emailSetting.socialMediaLink}
                </SettingItemLink>
              </Box>
            </StyledWhiteInsidePadding>
          </StyledWhiteContainer>
        );
      } else {
        return (
          <StyledWhiteContainer>
            <StyledWhiteInsidePadding>
              <Box
                onClick={() => setExpanded(!expanded)}
                display={"flex"}
                alignItems={"center"}
                sx={{ cursor: "pointer" }}
              >
                <Box>
                  <StyledSettingItemTitle>
                    {formatMessage({ id: "notification.create.emailFooter" })}
                    <Box sx={{ fontWeight: 400, marginLeft: "4px" }}>
                      ({formatMessage({ id: "global.form.recommended" })})
                    </Box>
                  </StyledSettingItemTitle>
                  <StyledSettingItemDescription sx={{ marginBottom: "0px" }}>
                    {formatMessage({
                      id: "notification.create.emailFooterDescription",
                    })}
                  </StyledSettingItemDescription>
                </Box>
                <Box
                  sx={{ transform: `rotate(${expanded ? "180deg" : "0deg"})` }}
                >
                  <Box
                    component="i"
                    className="meta-crm-icon-ic_arrow-down font-size-24"
                    sx={{
                      color: theme.customColors.grey[700],
                    }}
                  />
                </Box>
              </Box>

              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box mt="20px" mb="20px">
                  <StyledInputTitle>
                    {formatMessage({
                      id: "notification.create.emailFooter.address",
                    })}
                  </StyledInputTitle>
                  <InputField
                    placeholder={formatMessage({
                      id: "notification.create.emailFooter.addressPlaceHolder",
                    })}
                    name="companyAddress"
                    control={control}
                    value={formData.emailSetting.companyAddress}
                    onChange={handleFieldChangeEmail}
                  />
                </Box>

                <Box mb="20px">
                  <StyledInputTitle>
                    {formatMessage({
                      id: "notification.create.emailFooter.ppLink",
                    })}
                  </StyledInputTitle>
                  <InputField
                    placeholder={formatMessage({
                      id: "notification.create.emailFooter.ppLinkPlaceHolder",
                    })}
                    name="privacyPolicyLink"
                    control={control}
                    value={formData.emailSetting.privacyPolicyLink}
                    onChange={handleFieldChangeEmail}
                  />
                </Box>

                <Box mb="20px">
                  <StyledInputTitle>
                    {formatMessage({
                      id: "notification.create.emailFooter.email",
                    })}
                  </StyledInputTitle>
                  <InputField
                    placeholder={formatMessage({
                      id: "notification.create.emailFooter.emailPlaceHolder",
                    })}
                    name="emailAddress"
                    control={control}
                    value={formData.emailSetting.emailAddress}
                    onChange={handleFieldChangeEmail}
                  />
                </Box>

                <Box mb="20px">
                  <StyledInputTitle>
                    {formatMessage({
                      id: "notification.create.emailFooter.webUrl",
                    })}
                  </StyledInputTitle>
                  <InputField
                    placeholder={formatMessage({
                      id: "notification.create.emailFooter.webUrlPlaceHolder",
                    })}
                    name="websiteURL"
                    control={control}
                    value={formData.emailSetting.websiteURL}
                    onChange={handleFieldChangeEmail}
                  />
                </Box>

                <Box>
                  <StyledInputTitle>
                    {formatMessage({
                      id: "notification.create.emailFooter.socialLinks",
                    })}
                  </StyledInputTitle>
                  <InputField
                    placeholder={formatMessage({
                      id: "notification.create.emailFooter.socialLinkPlaceHolder",
                    })}
                    name="socialMediaLink"
                    control={control}
                    value={formData.emailSetting.socialMediaLink}
                    onChange={handleFieldChangeEmail}
                  />
                </Box>
              </Collapse>
            </StyledWhiteInsidePadding>
          </StyledWhiteContainer>
        );
      }
    } else {
      return null;
    }
  };

  return (
    <Box>
      {readOnly && (
        <Box
          sx={{
            padding: "16px",
            backgroundColor: alpha(theme.customColors.purple[500], 0.15),
            borderRadius: "4px",
            border: `1px solid ${theme.customColors.purple[500]}`,
            display: "flex",
            marginBottom: "8px",
          }}
        >
          <Box mr={1}>
            <Box
              component="i"
              className="meta-crm-icon-ic_info_fill font-size-20"
              color={theme.customColors.purple[500]}
            />
          </Box>
          <Box sx={{ color: "#222", lineHeight: "normal" }}>
            {formatMessage({
              id: "notification.create.sentTips",
            })}
          </Box>
        </Box>
      )}

      <StyledWhiteContainer>
        <StyledWhiteInsidePadding>
          {readOnly ? (
            <>
              <StyledSettingItemTitle readonly>
                {formatMessage({ id: "notification.create.scheduleSendTime" })}
              </StyledSettingItemTitle>
              <StyledSettingItemInfo>
                {moment(formData.sendTime).format("MMM D, YYYY HH:mm")}
              </StyledSettingItemInfo>
            </>
          ) : (
            <>
              {" "}
              <Box
                display={"flex"}
                alignItems={"flex-start"}
                justifyContent={"space-between"}
              >
                <Box>
                  <StyledSettingItemTitle>
                    {formatMessage({ id: "notification.create.scheduleTitle" })}
                  </StyledSettingItemTitle>
                  <StyledSettingItemDescription
                    sx={{
                      ...(!formData.isSchedule && {
                        marginBottom: "0",
                      }),
                    }}
                  >
                    {formatMessage({
                      id: "notification.create.scheduleDescription",
                    })}
                  </StyledSettingItemDescription>
                </Box>
                <Controller
                  name="isSchedule"
                  control={control}
                  render={({
                    field: {
                      onChange: onFormChange,
                      value: formValue,
                      ...field
                    },
                  }) => {
                    return (
                      <Switch
                        checked={formData.isSchedule}
                        iosSwitch
                        onChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            isSchedule: value,
                          }));
                          onFormChange(value);
                        }}
                      />
                    );
                  }}
                />
              </Box>
              {formData.isSchedule && (
                <>
                  <StyledInputTitle
                    isRequired={true}
                    errors={errorsInfo.sendTime}
                  >
                    {formatMessage({
                      id: "notification.create.scheduleSendTime",
                    })}
                  </StyledInputTitle>
                  <Box>
                    <Controller
                      name="sendTime"
                      control={control}
                      rules={{
                        required: formatMessage({
                          id: "notification.create.blankFieldError",
                        }),
                      }}
                      render={({
                        field: { onChange: onFormChange, ...field },
                      }) => (
                        <DateTimePicker
                          {...field}
                          errors={errorsInfo.sendTime}
                          value={formData.sendTime}
                          showTimeSelect
                          onChange={(value) => {
                            handleChangeStartFrom(value);
                            onFormChange(value);
                          }}
                          timeFormat="HH:mm"
                          timeIntervals={15}
                          timeCaption="time"
                          minDate={new Date()} // 禁用今天以前的日期
                          minTime={
                            !formData.sendTime || isToday(formData.sendTime)
                              ? new Date()
                              : null
                          } // 只有當選擇了日期且是今天時，才應用 minTime
                          maxTime={
                            !formData.sendTime || isToday(formData.sendTime)
                              ? new Date(new Date().setHours(23, 59, 59, 999))
                              : null
                          } // 只有當選擇了日期且是今天時，才應用 maxTime
                          dateFormat="MM/d/yyyy h:mm aa"
                          showIcon
                          icon={
                            <Box
                              component="i"
                              color={(theme) => theme.customColors.grey[700]}
                              className="meta-crm-icon-ic_calendar font-size-18"
                            />
                          }
                          placeholderText={formatMessage({
                            id: "campaignBuild.secondStep.expirationDate",
                          })}
                        />
                      )}
                    />
                    {errorsInfo.sendTime && (
                      <p style={{ color: "red" }}>
                        {errorsInfo.sendTime.message}
                      </p>
                    )}
                  </Box>
                </>
              )}
            </>
          )}
        </StyledWhiteInsidePadding>
      </StyledWhiteContainer>

      <StyledWhiteContainer>
        <StyledWhiteInsidePadding>
          {readOnly ? (
            <>
              <StyledSettingItemTitle readonly>
                {formatMessage({ id: "notification.global.targetAudience" })}
              </StyledSettingItemTitle>
              <StyledSettingItemInfo>
                {formData?.targetAudiences?.length > 0 ? (
                  <Box display="flex" alignItems="center" flexWrap="wrap">
                    {formData.targetAudiences.map((tag) => (
                      <MetaCRMTag
                        key={tag.value}
                        option={{
                          color: get(tag, "color", "#E6E6E6"),
                          label: tag.label || tag.value,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  "-"
                )}
              </StyledSettingItemInfo>
            </>
          ) : (
            <>
              {" "}
              <StyledSettingItemTitle>
                {formatMessage({ id: "notification.global.targetAudience" })}
              </StyledSettingItemTitle>
              <StyledSettingItemDescription>
                {formatMessage({
                  id: "notification.create.audienceDescription",
                })}
              </StyledSettingItemDescription>
              <StyledInputTitle
                isRequired={true}
                errors={errorsInfo.targetAudiences}
              >
                {formatMessage({ id: "notification.create.selectAudience" })}
              </StyledInputTitle>
              <Box>
                <AutoCompleteDropdown
                  name={"targetAudiences"}
                  control={control}
                  multiple
                  isTag
                  value={formData.targetAudiences}
                  placeholder={formatMessage({ id: "menu.placeholder.select" })}
                  open={audienceOpen}
                  onClick={handleToggle}
                  onClose={() => {
                    setAudienceOpen(false);
                  }}
                  options={audienceOptions}
                  onChange={handleChangeTargetAudiences}
                  loading={audienceOptionsLoading}
                  limitTags={3}
                  size={SIZE.XL}
                  errors={errorsInfo.targetAudiences}
                  noRenderErrorText
                  rules={{
                    required: formatMessage({
                      id: "notification.create.blankFieldError",
                    }),
                  }}
                />
                <Box>
                  {errorsInfo.targetAudiences && (
                    <p style={{ color: "red" }}>
                      {errorsInfo.targetAudiences.message}
                    </p>
                  )}
                </Box>
              </Box>
            </>
          )}
        </StyledWhiteInsidePadding>
      </StyledWhiteContainer>

      <StyledWhiteContainer>
        <StyledWhiteInsidePadding>
          {readOnly ? (
            <>
              <StyledSettingItemTitle readonly>
                {formatMessage({ id: "notification.create.channelTitle" })}
              </StyledSettingItemTitle>
              <Box>
                {renderSelectChannels({
                  field: {},
                })}
              </Box>
            </>
          ) : (
            <>
              {" "}
              <StyledSettingItemTitle>
                {formatMessage({ id: "notification.create.channelTitle" })}
              </StyledSettingItemTitle>
              <StyledSettingItemDescription>
                {formatMessage({
                  id: "notification.create.channelDescription",
                })}
              </StyledSettingItemDescription>
              <StyledInputTitle isRequired={true} errors={errorsInfo.channels}>
                {formatMessage({ id: "notification.create.selectChannels" })}
              </StyledInputTitle>
              <Box>
                <Controller
                  name="channels"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: "必選",
                    },
                    validate: (value) => {
                      return (
                        Object.values(value).some((value) => value === true) ||
                        formatMessage({
                          id: "notification.create.selectChannelsError",
                        })
                      );
                    },
                  }}
                  render={renderSelectChannels}
                />
                <Box>
                  {errorsInfo.channels && (
                    <Box color={theme.customColors.pink[600]}>
                      {formatMessage({
                        id: "notification.create.selectChannelsError",
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </StyledWhiteInsidePadding>
      </StyledWhiteContainer>

      <StyledWhiteContainer>
        <StyledWhiteInsidePadding>
          {readOnly ? (
            <>
              <StyledSettingItemTinyTitle sx={{ marginBottom: "16px" }}>
                {formatMessage({
                  id: "notification.create.contentSectionTitle",
                })}
              </StyledSettingItemTinyTitle>
              <Box mb="12px">
                <StyledSettingItemTitle readonly>
                  {formatMessage({
                    id: "notification.create.notificationTitle",
                  })}
                </StyledSettingItemTitle>
                <StyledSettingItemInfo>{formData.title}</StyledSettingItemInfo>
              </Box>

              <Box mb="20px">
                <StyledSettingItemTitle readonly>
                  {formatMessage({ id: "notification.create.contentField" })}
                </StyledSettingItemTitle>
                <StyledSettingItemInfo>
                  {formData.content}
                </StyledSettingItemInfo>
              </Box>

              <StyledSettingItemTitle readonly>
                {formatMessage({ id: "global.commonWord.image" })}
              </StyledSettingItemTitle>
              <Box width={"100%"}>
                <Box
                  component={"img"}
                  src={formData.image}
                  width={"100%"}
                ></Box>
              </Box>
            </>
          ) : (
            <>
              {" "}
              <StyledSettingItemTitle>
                {formatMessage({
                  id: "notification.create.contentSectionTitle",
                })}
              </StyledSettingItemTitle>
              <StyledSettingItemDescription>
                {formatMessage({
                  id: "notification.create.contentSectionDescription",
                })}
              </StyledSettingItemDescription>
              {(formData.channels.telegram || formData.channels.discord) && (
                <Box
                  sx={{
                    padding: "16px",
                    backgroundColor: alpha(
                      theme.customColors.purple[500],
                      0.15
                    ),
                    borderRadius: "4px",
                    border: `1px solid ${theme.customColors.purple[500]}`,
                    display: "flex",
                    marginBottom: "20px",
                  }}
                >
                  <Box mr={1}>
                    <Box
                      component="i"
                      className="meta-crm-icon-ic_info_fill font-size-20"
                      color={theme.customColors.purple[500]}
                    />
                  </Box>
                  <Box sx={{ color: "#222", lineHeight: "normal" }}>
                    {formatMessage({
                      id: "notification.create.contentSectionTips",
                    })}
                  </Box>
                </Box>
              )}
              <Box mb="20px">
                <StyledInputTitle isRequired={true} errors={errorsInfo.title}>
                  {formatMessage({
                    id: "notification.create.notificationTitle",
                  })}
                </StyledInputTitle>
                <InputField
                  placeholder={formatMessage({
                    id: "notification.create.notificationTitlePlaceHolder",
                  })}
                  name="title"
                  value={formData.title}
                  onChange={handleFieldChange}
                  errors={get(errorsInfo, "title.message")}
                  control={control}
                  maxLength={50}
                  rules={{
                    required: {
                      value: true,
                      message: formatMessage({
                        id: "notification.create.blankFieldError",
                      }),
                    },
                  }}
                />
              </Box>
              <Box mb="20px">
                <StyledInputTitle isRequired={true} errors={errorsInfo.content}>
                  {formatMessage({ id: "notification.create.contentField" })}
                </StyledInputTitle>
                <InputField
                  multiline={true}
                  rows={8}
                  maxLength={2000}
                  placeholder={formatMessage({
                    id: "notification.create.contentFieldPlaceHolder",
                  })}
                  name="content"
                  control={control}
                  value={formData.content}
                  onChange={handleFieldChange}
                  errors={get(errorsInfo, "content.message")}
                  rules={{
                    required: {
                      value: true,
                      message: formatMessage({
                        id: "notification.create.blankFieldError",
                      }),
                    },
                  }}
                />
              </Box>
              <StyledInputTitle>
                {formatMessage({ id: "notification.create.imageField" })}
              </StyledInputTitle>
              <Box sx={{ overflow: "hidden" }}>
                <ImageUploader
                  onChange={(images) => uploadFile(images[0].file)}
                  previewWidth="210px"
                  value={formData.image ? [formData.image] : []}
                  multiple={false}
                  uploadImgLoading={uploadImgLoading}
                  height="300px"
                  width="100%"
                  setImagesError={(errors) => setImagesError(errors)}
                  onDelete={handleDeleteImage}
                  acceptType={["png", "jpg", "jpeg", "gif"]}
                  uploadText={
                    <Box
                      display={"flex"}
                      flexDirection="column"
                      alignItems={"center"}
                    >
                      <Box
                        sx={{
                          fontWeight: 700,
                          color: "#222",
                          fontSize: "14px",
                          marginBottom: "4px",
                        }}
                      >
                        {formatMessage({ id: "global.btn.uploadFile" })}
                      </Box>
                      <Box>
                        {formatMessage({
                          id: "global.btn.uploadFileDescription",
                        })}
                      </Box>
                    </Box>
                  }
                  uploadIcon={
                    <Box
                      component="i"
                      className="meta-crm-icon-ic_upload font-size-44"
                      color={(theme) => theme.customColors.purple[500]}
                      marginBottom="12px"
                    />
                  }
                />
              </Box>
            </>
          )}
        </StyledWhiteInsidePadding>

        {renderCTAButtonArea()}

        {!readOnly && (
          <Box sx={{ borderTop: `1px solid ${theme.customColors.grey[300]}` }}>
            <StyledWhiteInsidePadding>
              <Box display={"flex"}>
                <Controller
                  name="appendUTM"
                  control={control}
                  render={({
                    field: {
                      onChange: onFormChange,
                      value: formValue,
                      ...field
                    },
                  }) => {
                    return (
                      <Checkbox
                        checkboxSize={14}
                        mr={8}
                        checked={formData.appendUTM}
                        label={formatMessage({
                          id: "notification.create.utmConsent",
                        })}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            appendUTM: e.target.checked,
                          }));
                          onFormChange(e.target.checked);
                        }}
                        controlStyle={{
                          alignItems: "flex-start !important",
                          color: theme.customColors.grey[700],
                          "& .MuiTypography-root": {
                            lineHeight: `1.1 !important`,
                          },
                        }}
                      ></Checkbox>
                    );
                  }}
                />
              </Box>
            </StyledWhiteInsidePadding>
          </Box>
        )}
      </StyledWhiteContainer>

      {renderEmailBottomArea()}

      <FixedButton leftComponent={renderLeftButton()}>
        {renderDraftButton()}

        {renderSendButton()}
      </FixedButton>

      <BasicDialog
        dialogRootStyle={{ width: "350px", maxWidth: "100%" }}
        closeBtnShow={false}
        open={timePassDialogShow}
        show={timePassDialogShow}
      >
        <Box
          padding="30px 48px"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{ fontSize: "18px", fontWeight: "700", marginBottom: "12px" }}
            color={(theme) => theme.customColors.grey[700]}
          >
            {formatMessage({ id: "notification.create.scheduleReminder" })}
          </Box>

          <Box
            color={(theme) => theme.customColors.grey[600]}
            sx={{ textAlign: "center", marginBottom: "16px" }}
          >
            {formatMessage({
              id: "notification.create.scheduleReminderDescription",
            })}
          </Box>

          <Box>
            <Button
              sx={{ width: "120px", marginRight: "8px" }}
              onClick={() => {
                setTimePassDialogShow(false);
              }}
            >
              {formatMessage({ id: "global.btn.gotIt" })}
            </Button>
          </Box>
        </Box>
      </BasicDialog>
      <Prompt when={isDirty} />
    </Box>
  );
};

export default NotificationInfoArea;
